import type { CalendarEvent, FamilyMember } from '../../../shared/types';
import type { Resource } from '../useResource';
import { memberById, upcoming } from '../homeData';
import { whenLabel } from '../dates';
import { Card, CardTitle, Empty, ErrorState, LoadingRows } from '../ui';

/**
 * What's next on the family calendar. Shared by both views.
 *
 * `family` is optional: adults can read the roster (adult-gated /api/family) so
 * their events get the member's own color; kids can't, so their events render
 * without one rather than with a made-up one.
 */
export default function UpNextCard({
  res,
  family,
  limit = 3,
}: {
  res: Resource<CalendarEvent[]>;
  family?: FamilyMember[] | null;
  limit?: number;
}) {
  const { data, loading, error, reload } = res;
  const events = upcoming(data, limit);

  return (
    <Card className="p-5">
      <CardTitle icon="calendar" action={{ to: '/calendar', label: 'Calendar' }}>
        Up next
      </CardTitle>

      <div className="mt-4">
        {loading && <LoadingRows rows={3} />}
        {!loading && error && <ErrorState what="the calendar" onRetry={reload} />}

        {!loading && !error && events.length === 0 && (
          <Empty
            message="Nothing on the calendar for the next week — either it's a quiet one, or Google Calendar isn't connected yet."
            action={{ to: '/calendar', label: 'Open the calendar' }}
          />
        )}

        {!loading && !error && events.length > 0 && (
          <ul className="space-y-3">
            {events.map((e) => {
              const member = memberById(family ?? null, e.member_id);
              return (
                <li key={e.id} className="flex items-start gap-3">
                  <span
                    className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: member?.color ?? 'var(--color-ink-faint)' }}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{e.title}</p>
                    <p className="text-sm text-ink-soft">
                      {whenLabel(e.starts_at, e.all_day)}
                      {member && ` · ${member.name}`}
                      {e.location && ` · ${e.location}`}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
